import React, { useState } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import axios from "axios";
import { API_BASE_URL, API_URLS } from "constants/api";
import { Title } from "components/Auth/components/Title";
import { Text } from "components/Auth/components/Text";
import { Input } from "components/Auth/components/Input";
import { Button } from "ui-kit/Button";
import { SmartForm } from "ui-kit/SmartForm";
import { Layout } from "../Layout";
import { NewPasswordCompleted } from "./Completed";
import IntlStore from "stories/IntlStore";
import {
  structuredValidator,
  hasUpperCase,
  compose,
  hasRequiredLength,
  isShowInputError,
  comparisonValues
} from "utils/validation";

interface FormValues {
  password: string;
  confirmPassword: string;
}

const MIN_PASSWORD_LENGTH = 8;

export const NewPassword = () => {
  const [completed, setCompleted] = useState(false);
  const search = useLocation().search;

  const onSubmit = async (values: FormValues) => {
    try {
      const params = Object.fromEntries(new URLSearchParams(search.replace(/\+/g, "%2B")));
      await axios.post(
        `${API_BASE_URL}${API_URLS.ResetPassword}`,
        {
          token: params.token,
          email: params.email,
          password: values.password,
          password_confirmation: values.confirmPassword
        }
      );
      setCompleted(true);
    } catch (e: any) {
      const message = e.response.data.message;
      console.error("Error", message);
      toast.error(message);
    }
  };

  if (completed) {
    return <NewPasswordCompleted />;
  }

  return (
    <Layout>
      <SmartForm<FormValues>
        onSubmit={onSubmit}
        initialValues={{ password: '', confirmPassword: '' }}
        validate={structuredValidator({
          password: compose(hasUpperCase, hasRequiredLength(MIN_PASSWORD_LENGTH)),
          confirmPassword: compose(hasUpperCase, hasRequiredLength(MIN_PASSWORD_LENGTH), comparisonValues('password'))
        })}
      >
        {props => {
          const { handleSubmit, errors, submitting } = props;
          return (
            <Container id="new-password" onSubmit={handleSubmit}>
              <Title>
                <FormattedMessage id="Auth.NewPassword.Title" defaultMessage="Create a new password"/>
              </Title>
              <Subtitle>
                <FormattedMessage id="Auth.NewPassword.Subtitle" defaultMessage="Enter a new password for your Slash Music account"/>
              </Subtitle>
              <InputContainer>
                <Input
                  name="password"
                  type="password"
                  placeholder={IntlStore.intl.formatMessage({
                    id: "Auth.NewPassword.NewPassword",
                    defaultMessage: "New password"
                  })}
                  error={isShowInputError<FormValues>(props, 'password') && <FormattedMessage id={errors?.password} values={{characters: MIN_PASSWORD_LENGTH}} />}
                />
                <Input
                  name="confirmPassword"
                  type="password"
                  placeholder={IntlStore.intl.formatMessage({
                    id: "Auth.NewPassword.ConfirmPassword",
                    defaultMessage: "Confirm password"
                  })}
                  error={isShowInputError<FormValues>(props, 'confirmPassword') && <FormattedMessage id={errors?.confirmPassword} values={{characters: MIN_PASSWORD_LENGTH}} />}
                />
              </InputContainer>
              <Button type="submit" form="new-password" loading={submitting}>
                <FormattedMessage id="Auth.NewPassword.Button" defaultMessage="Update password"/>
              </Button>
            </Container>
          );
        }}
      </SmartForm>

    </Layout>
  );
};

const Container = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  padding: 54px;
  height: 100%;
  max-width: 548px;

  @media (max-width: 1200px) {
    max-width: 100%;
  }
`;

const Subtitle = styled(Text)`
  font-size: 16px;
  line-height: 26px;
  margin-bottom: 10px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

